import { Box } from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";

import { EXPANDED, MOBILE_BREAKPOINT, RAIL } from "./constants";
import { useSidebarChrome } from "./SidebarChromeContext";

type Props = { children?: React.ReactNode; styles?: any };

/**
 * The per-page layout shell: spacer + content area.
 *
 * WHAT MOVED OUT OF HERE
 * ----------------------
 * The nav panel itself now lives in `SidebarChromeProvider`, mounted once by
 * the app layout route (see src/layouts/AppLayout.tsx). Rendering it per page
 * meant React destroyed and rebuilt it on every navigation, which refetched
 * the company, reset the menu's scroll position, and broke the active-strip
 * slide animation. The full reasoning is in SidebarChrome.tsx.
 *
 * The PUBLIC API IS UNCHANGED — `<Sidebar styles={{ sidebarRoot,
 * sidebarRightRoot }}>{page}</Sidebar>` behaves exactly as before, so the 24
 * pages that use it did not have to be touched. What this component renders is
 * now only what genuinely belongs to the page: the strip of width reserved for
 * a pinned panel, and the scroll container the page content lives in.
 */
export const Sidebar: React.FC<Props> = ({ children, styles }) => {
  const { pinned, isMobile } = useSidebarChrome();
  const reduceMotion = useReducedMotion();

  return (
    <Box
      className={styles?.sidebarRoot}
      sx={{
        display: "flex",
        height: "var(--app-vh)",
        width: "100%",
        overflow: "hidden",
        position: "relative",
        [`@media (max-width:${MOBILE_BREAKPOINT - 0.05}px)`]: {
          height: "auto",
          minHeight: "var(--app-vh)",
          overflow: "visible",
        },
      }}
    >
      {/* ── DESKTOP: spacer that reserves the panel's footprint ── */}
      {/*
        Pinning and hovering are different intents, and the spacer is what
        separates them. Pinned means "keep this open", so the content shifts
        and nothing is covered. Hovering is a transient peek, so the panel
        overlays and the layout stays still — reflowing the whole page every
        time the pointer crossed the left edge would be unusable.

        Previously the spacer was always RAIL, so a pinned sidebar sat on top
        of the page content permanently.
      */}
      {!isMobile && (
        <Box
          component={motion.div}
          aria-hidden
          initial={false}
          animate={{ width: pinned ? EXPANDED : RAIL }}
          transition={
            reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 320, damping: 32 }
          }
          sx={{ flexShrink: 0, height: "100%" }}
        />
      )}

      {/* ── Content area ── */}
      <Box
        className={styles?.sidebarRightRoot}
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          [`@media (max-width:${MOBILE_BREAKPOINT - 0.05}px)`]: {
            overflowY: "auto",
            overflowX: "hidden",
            WebkitOverflowScrolling: "touch",
            height: "auto",
            minHeight: "var(--app-vh)",
          },
        }}
      >
        {children}
      </Box>
    </Box>
  );
};
